import { useInspectorNodeId } from './useInspectorNode';

/**
 * Custom hook that provides helper functions for inspector components.
 * This hook is separated from useInspectorNode to follow single responsibility principle
 * and to allow optional usage only where specific helpers are needed.
 *
 * @returns Object containing helper functions for inspector manipulation
 *
 * @example
 * // Usage in color fields that affect borders
 * const { id, props, actions } = useInspectorNode<BorderColorProps>(...);
 * const { ensureStrokeVisible } = useInspectorHelper();
 *
 * // Ensure border is visible when setting a color
 * ensureStrokeVisible<StylingProps>('borderWidth', props.borderWidth);
 */
export function useInspectorHelper() {
  const { id, actions } = useInspectorNodeId();

  /**
   * Helper to ensure stroke/border is visible when setting a color.
   * Sets the width property to 1 if it's currently 0.
   * Uses history.merge() to combine with the previous history entry.
   *
   * @param widthPropName The name of the width property (e.g., 'borderWidth', 'fieldInputBorderWidth')
   * @param currentWidth The current value of the width property
   */
  const ensureStrokeVisible = <P extends object>(widthPropName: keyof P, currentWidth: number) => {
    if (currentWidth === 0) {
      actions.history.merge().setProp(id, (p: P) => {
        (p as Record<string, unknown>)[widthPropName as string] = 1;
      });
    }
  };

  return {
    ensureStrokeVisible,
  };
}
